import { Gateway, IntegrationSystem, TransactionResult, TransactionState, TransactionUserAction, } from '@biteinc/enums';
export var CbordAccountType;
(function (CbordAccountType) {
    CbordAccountType[CbordAccountType["MealPlan"] = 1] = "MealPlan";
    CbordAccountType[CbordAccountType["IncliningBalance"] = 2] = "IncliningBalance";
    CbordAccountType[CbordAccountType["DecliningBalance"] = 3] = "DecliningBalance";
})(CbordAccountType || (CbordAccountType = {}));
function isBalanceUnlimitedCbordAccount(accountType) {
    const cbordAccountType = Number(accountType);
    return (cbordAccountType === CbordAccountType.MealPlan ||
        cbordAccountType === CbordAccountType.IncliningBalance);
}
export function balanceLimitsStoredValueSpend(system) {
    // FreedomPay SV prod can approve balance inquiry without returning accountBalance,
    // unlike sandbox. Let charge/partial approval decide instead of blocking on inquiry.
    if (system === IntegrationSystem.FreedomPayKioskStoredValue ||
        system === IntegrationSystem.FreedomPayStoredValue) {
        return false;
    }
    return true;
}
// AtriumAccountType.MealExchange in @biteinc/i9ns (not importable from this package).
const ATRIUM_MEAL_EXCHANGE_ACCOUNT_TYPE = 2;
/**
 * Campus-card tenders whose balance is a redemption COUNT, not a money amount. The
 * standard campus-card account types are declining balance, meal exchange, and cash
 * equivalency; providers map onto them with their own balance semantics — Atrium meal
 * exchange balances are MEL meal counts, while e.g. TSE meal plans report dollars.
 * Clients must not render count balances as prices.
 */
export function campusCardTenderBalanceIsCount(system, accountType) {
    return (system === IntegrationSystem.AtriumCampusCard &&
        Number(accountType) === ATRIUM_MEAL_EXCHANGE_ACCOUNT_TYPE);
}
export function balanceLimitsCampusCardSpend(system, accountType) {
    return !((system === IntegrationSystem.CbordCampusCard && isBalanceUnlimitedCbordAccount(accountType)) ||
        // A count balance must not cap the requested charge amount (the server charges
        // exactly one meal regardless of the requested total).
        campusCardTenderBalanceIsCount(system, accountType));
}
export function transactionIsSuccessful(data, { allowPartialApprovals, allowOfflineApprovals, }) {
    const resultValues = [TransactionResult.Approved];
    if (allowPartialApprovals) {
        resultValues.push(TransactionResult.PartiallyApproved);
    }
    if (allowOfflineApprovals) {
        resultValues.push(TransactionResult.OfflineApproved);
    }
    if (!resultValues.includes(data.result)) {
        return false;
    }
    const stateValues = [TransactionState.Committed];
    if (gatewayUsesAuthAndCapture(data.gateway)) {
        stateValues.push(TransactionState.Uncommitted);
    }
    if (!stateValues.includes(data.state)) {
        return false;
    }
    if (TransactionUserAction.Cancelled === data.userAction ||
        TransactionUserAction.Abandoned === data.userAction) {
        return false;
    }
    return true;
}
export function isOfflineApproval(data) {
    return data.result === TransactionResult.OfflineApproved;
}
export function gatewayUsesAuthAndCapture(gateway) {
    switch (gateway) {
        case Gateway.Adyen:
        case Gateway.Archer:
        case Gateway.AtriumLoyalty:
        case Gateway.AtriumStoredValue:
        case Gateway.CardConnect:
        case Gateway.CbordStoredValue:
        case Gateway.Chase:
        case Gateway.ChoptLoyalty:
        case Gateway.Como:
        case Gateway.CreditCall:
        case Gateway.Eigen:
        case Gateway.EigenStoredValue:
        case Gateway.Elavon:
        case Gateway.FreedomPayFlash:
        case Gateway.FreedomPayKiosk:
        case Gateway.FreedomPayStoredValue:
        case Gateway.Givex:
        case Gateway.GivexLoyalty:
        case Gateway.Glory:
        case Gateway.Heartland:
        case Gateway.LevelUp:
        case Gateway.LovesRewards:
        case Gateway.Moneris:
        case Gateway.None:
        case Gateway.OLOGift:
        case Gateway.OloPayKioskOrderingApi:
        case Gateway.OloPayKioskPaymentApi:
        case Gateway.ParPay:
        case Gateway.PaytronixGift:
        case Gateway.PinataCashMachine:
        case Gateway.PinataLoyalty:
        case Gateway.PinataPay:
        case Gateway.PinataPayNative:
        case Gateway.PinataCampusCard:
        case Gateway.PinataStoredValue:
        case Gateway.TransactCampusCard:
        case Gateway.Punchh:
        case Gateway.Appfront:
        case Gateway.SmoothCommerce:
        case Gateway.Spendgo:
        case Gateway.Square:
        case Gateway.Starbucks:
        case Gateway.StarbucksStoredValue:
        case Gateway.Stripe:
        case Gateway.StripeTerminal:
        case Gateway.TCC:
        case Gateway.QuickchargeCampusCard:
        case Gateway.Thanx:
        case Gateway.Valutec:
        case Gateway.VerifonePointCloud:
        case Gateway.VerifonePointSca:
        case Gateway.Worldpay:
            return false;
    }
}
export function gatewaySupportsPartialRefunds(gateway) {
    switch (gateway) {
        case Gateway.Adyen:
        case Gateway.AtriumLoyalty:
        case Gateway.AtriumStoredValue:
        case Gateway.CardConnect: // Only if the payment is fully captured
        case Gateway.CbordStoredValue: // TODO - verify this is true
        case Gateway.Chase:
        case Gateway.CreditCall:
        case Gateway.Eigen:
        case Gateway.EigenStoredValue:
        case Gateway.Elavon:
        case Gateway.FreedomPayFlash:
        case Gateway.FreedomPayKiosk:
        case Gateway.FreedomPayStoredValue:
        case Gateway.Heartland:
        case Gateway.Moneris:
        case Gateway.None:
        case Gateway.OloPayKioskOrderingApi:
        case Gateway.OloPayKioskPaymentApi:
        case Gateway.ParPay:
        case Gateway.PinataPay:
        case Gateway.PinataPayNative:
        case Gateway.PinataCampusCard:
        case Gateway.PinataStoredValue:
        case Gateway.PinataLoyalty:
        case Gateway.Punchh:
        case Gateway.Square:
        case Gateway.Stripe:
        case Gateway.StripeTerminal:
        case Gateway.TransactCampusCard:
        case Gateway.VerifonePointCloud:
        case Gateway.VerifonePointSca:
        case Gateway.Worldpay:
            return true;
        case Gateway.Archer:
        case Gateway.ChoptLoyalty:
        case Gateway.Como:
        case Gateway.Givex:
        case Gateway.GivexLoyalty:
        case Gateway.Glory:
        case Gateway.LevelUp:
        case Gateway.LovesRewards:
        case Gateway.OLOGift:
        case Gateway.PaytronixGift: // TODO: ??
        case Gateway.PinataCashMachine:
        case Gateway.Spendgo:
        case Gateway.TCC:
        case Gateway.Appfront:
        case Gateway.SmoothCommerce:
        case Gateway.Starbucks:
        case Gateway.StarbucksStoredValue:
        case Gateway.Thanx:
        case Gateway.QuickchargeCampusCard:
        case Gateway.Valutec:
            return false;
    }
}
//# sourceMappingURL=universal_payment_helper.js.map